import type { RangePickerProps } from 'antd/es/date-picker';
import { HTMLAttributes, KeyboardEvent, MouseEvent, RefObject, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  constraintsOf,
  formatDate,
  fromDisplayTz,
  precisionOfProps,
  toPair,
  toPartial,
  toRange,
  type AntdPair,
  type XmRangeDatePickerProps,
  type XmRangeDatePickerRef,
} from './rangePickerAntd';
import {
  createRangePickerState,
  endpointOf,
  isSameRange,
  rangePickerReducer,
  shownRange,
  type EndpointRole,
  type PartialRangeValue,
  type RangeEndpoint,
  type RangePickerEffect,
  type RangePickerEvent,
  type RangePickerState,
} from './rangePickerState';

type Gesture = 'click' | 'preset' | 'focus' | 'typing' | `key:${string}` | null;

/** Gesture in flight; expires once its DOM event finished dispatching (eventPhase NONE). */
interface GestureEntry {
  readonly gesture: Gesture;
  readonly event: Event;
}

type AdapterPickerProps = Pick<
  RangePickerProps,
  'value' | 'open' | 'onOpenChange' | 'onCalendarChange' | 'onChange' | 'onFocus' | 'onBlur' | 'onKeyDown'
>;

interface FocusRequest {
  readonly endpoint: RangeEndpoint;
  readonly seq: number;
}

interface RangePickerAdapter {
  readonly state: RangePickerState;
  readonly wrapperProps: HTMLAttributes<HTMLDivElement>;
  readonly pickerProps: AdapterPickerProps;
  readonly okButtonProps: OkButtonProps;
}

interface OkButtonProps {
  readonly ref: RefObject<HTMLButtonElement | null>;
  readonly onClick: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

const GUARD_KEYS: readonly string[] = ['Tab', 'Enter', 'Escape'];
const INTERNAL_CLOSE: readonly Gesture[] = ['focus', 'key:Tab', 'key:Enter'];
const PRESETS_SELECTOR = '.ant-picker-presets';
const POPUP_SELECTOR = '.ant-picker-dropdown';

function initialState(props: XmRangeDatePickerProps): RangePickerState {
  const applied = toRange('value' in props ? props.value : props.defaultValue);
  return { ...createRangePickerState(applied), open: props.open ?? props.defaultOpen ?? false };
}

const isPresetTarget = (event: MouseEvent<HTMLDivElement>): boolean =>
  event.target instanceof Element && !!event.target.closest(PRESETS_SELECTOR);

/**
 * Drives the pure range picker state from antd RangePicker callbacks and runs the resulting effects.
 * @param props consumer props after the wrapper defaults were merged
 * @param picker ref of the rendered antd RangePicker
 * @returns props to spread on the gesture wrapper, on the RangePicker and on the OK button
 */
export function useRangePickerAdapter(props: XmRangeDatePickerProps, picker: RefObject<XmRangeDatePickerRef | null>): RangePickerAdapter {
  const [state, setState] = useState<RangePickerState>(() => initialState(props));
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [blurRequest, setBlurRequest] = useState<number>(0);
  const [resync, setResync] = useState<number>(0);
  const stateRef = useRef<RangePickerState>(state);
  const latest = useRef<XmRangeDatePickerProps>(props);
  const gestures = useRef<GestureEntry[]>([]);
  const ignoredOpen = useRef<boolean>(false);
  const okButton = useRef<HTMLButtonElement>(null);
  latest.current = props;

  const runEffect = useCallback((effect: RangePickerEffect): void => {
    const { onChange, format } = latest.current;
    switch (effect.type) {
      case 'emit':
        onChange?.(toPair(effect.range), effect.range ? [formatDate(effect.range[0], format), formatDate(effect.range[1], format)] : ['', '']);
        return;
      case 'focus':
        setFocusRequest(prev => ({ endpoint: effect.endpoint, seq: (prev?.seq ?? 0) + 1 }));
        return;
      case 'blur':
        setBlurRequest(prev => prev + 1);
        return;
    }
  }, []);

  const dispatch = useCallback((event: RangePickerEvent, silent = false): void => {
    const prev = stateRef.current;
    const { state: next, effects } = rangePickerReducer(prev, event, constraintsOf(latest.current));
    if (next !== prev) {
      stateRef.current = next;
      setState(next);
    }
    // A dropped entry leaves the shown pair as is, and antd would keep showing the rejected text.
    if (prev.typed && !next.typed && shownRange(next) === shownRange(prev)) setResync(count => count + 1);
    effects.forEach(runEffect);
    if (!silent && next.open !== prev.open) latest.current.onOpenChange?.(next.open);
  }, [runEffect]);

  const focusEndpoint = useCallback((role: EndpointRole | undefined): void => {
    const endpoint = endpointOf(role);
    if (endpoint !== null) dispatch({ type: 'focus', endpoint });
  }, [dispatch]);

  useEffect(() => {
    if (focusRequest) picker.current?.focus({ index: focusRequest.endpoint });
  }, [focusRequest, picker]);

  useEffect(() => {
    if (!blurRequest) return;
    picker.current?.blur();
    if (document.activeElement === okButton.current) okButton.current?.blur();
  }, [blurRequest, picker]);

  const controlled = 'value' in props;
  const externalValue = props.value;
  useEffect(() => {
    if (controlled) dispatch({ type: 'externalValue', range: toRange(externalValue) });
  }, [controlled, externalValue, dispatch]);

  const externalOpen = props.open;
  useEffect(() => {
    if (externalOpen === undefined || externalOpen === stateRef.current.open) return;
    dispatch({ type: externalOpen ? 'open' : 'close' }, true);
  }, [externalOpen, state.open, dispatch]);

  const readEntry = useCallback((): GestureEntry | null => {
    const stack = gestures.current;
    while (stack.length && stack[stack.length - 1].event.eventPhase === Event.NONE) stack.pop();
    return stack.length ? stack[stack.length - 1] : null;
  }, []);

  const isSelectorGesture = (entry: GestureEntry | null): boolean =>
    !!entry && entry.event.target instanceof Node && !!picker.current?.nativeElement.contains(entry.event.target);

  const outsideElement = (target: EventTarget | null): HTMLElement | null =>
    (target instanceof HTMLElement && !picker.current?.nativeElement.contains(target) && !target.closest(POPUP_SELECTOR) ? target : null);

  // Catches the portalled panel too: React routes events along the component tree, not the DOM.
  const wrapperProps = useMemo((): HTMLAttributes<HTMLDivElement> => {
    const enter = (gesture: Gesture, event: SyntheticEvent): void => {
      readEntry();
      gestures.current.push({ gesture, event: event.nativeEvent });
    };
    const onPointerCapture = (event: MouseEvent<HTMLDivElement>): void => {
      enter(isPresetTarget(event) ? 'preset' : 'click', event);
      ignoredOpen.current = false;
    };
    return {
      onMouseDownCapture: onPointerCapture,
      onClickCapture: onPointerCapture,
      onFocusCapture: event => enter('focus', event),
      onKeyDownCapture: event => {
        enter(GUARD_KEYS.includes(event.key) ? `key:${event.key}` : 'typing', event);
        ignoredOpen.current = false;
      },
      onChangeCapture: event => enter('typing', event),
    };
  }, [readEntry]);

  const onOpenChange = (next: boolean): void => {
    const entry = readEntry();
    if (next && !isSelectorGesture(entry)) {
      ignoredOpen.current = true;
      queueMicrotask(() => { ignoredOpen.current = false; });
      return;
    }
    if (!next && INTERNAL_CLOSE.includes(entry?.gesture ?? null)) return;
    dispatch({ type: next ? 'open' : 'close' });
  };

  /** antd re-reads `value` only on a new identity; mint one when its pair drifted from ours. */
  const resyncIfDrifted = (dates: AntdPair, shownBefore: PartialRangeValue | null): void => {
    const shown = shownRange(stateRef.current);
    if (shown === shownBefore && !isSameRange(dates, shown, precisionOfProps(latest.current))) setResync(prev => prev + 1);
  };

  const onCalendarChange: RangePickerProps['onCalendarChange'] = (dates, dateStrings, info) => {
    props.onCalendarChange?.(dates, dateStrings, info);
    const gesture = readEntry()?.gesture ?? null;
    if (gesture === 'key:Escape') return;
    const shownBefore = shownRange(stateRef.current);
    if (!stateRef.current.open && gesture !== 'typing') {
      resyncIfDrifted(dates, shownBefore);
      return;
    }
    if (gesture === 'preset') {
      const range = toRange(dates);
      if (range) dispatch({ type: 'preset', range });
      return;
    }
    focusEndpoint(info.range);
    const typing = gesture === 'typing';
    // antd parses typed text in the browser zone; the model orders and validates in the display zone.
    dispatch({ type: 'calendarChange', range: toPartial(typing ? fromDisplayTz(dates) : dates) ?? [null, null], source: typing ? 'typed' : 'panel' });
    if (!typing) resyncIfDrifted(dates, shownBefore);
  };

  const onChange: RangePickerProps['onChange'] = dates => {
    if (dates === null) dispatch({ type: 'clear' });
  };

  const onFocus: RangePickerProps['onFocus'] = (event, info) => {
    props.onFocus?.(event, info);
    if (ignoredOpen.current) {
      ignoredOpen.current = false;
      // rc-picker's close hands focus to the other field; give it back to where the user went.
      const origin = outsideElement(event.relatedTarget);
      if (origin) origin.focus({ preventScroll: true });
      else setBlurRequest(prev => prev + 1);
      return;
    }
    focusEndpoint(info.range);
  };

  const onBlur: RangePickerProps['onBlur'] = (event, info) => {
    props.onBlur?.(event, info);
    dispatch({ type: 'accept', source: 'blur' });
  };

  const onKeyDown: RangePickerProps['onKeyDown'] = (event, preventDefault) => {
    props.onKeyDown?.(event, preventDefault);
    if (event.key === 'Enter') dispatch({ type: 'accept', source: 'key' });
    if (event.key === 'Escape') dispatch({ type: 'close' });
    const { open, active } = stateRef.current;
    if (event.key === 'Tab' && !event.shiftKey && open && active === 1 && okButton.current) {
      event.preventDefault();
      okButton.current.focus();
    }
  };

  const onOkKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Escape') dispatch({ type: 'close' });
    if (event.key !== 'Tab' || !event.shiftKey) return;
    event.preventDefault();
    picker.current?.focus({ index: 1 });
  };

  const confirm = (): void => {
    dispatch({ type: 'confirm' });
    props.onOk?.(toPair(stateRef.current.applied) ?? [null, null]);
  };

  const okButtonProps: OkButtonProps = { ref: okButton, onClick: confirm, onKeyDown: onOkKeyDown };
  const shown = shownRange(state);
  // `resync` is read, not just listed: the React Compiler keeps only read values as memo deps.
  const value = useMemo(() => (resync >= 0 ? toPair(shown) : null), [shown, resync]);

  return {
    state,
    wrapperProps,
    pickerProps: { value, open: props.open ?? state.open, onOpenChange, onCalendarChange, onChange, onFocus, onBlur, onKeyDown },
    okButtonProps,
  };
}
